import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex min-w-0 select-none items-center justify-center gap-2.5 whitespace-nowrap !rounded-[14px] text-sm font-semibold !leading-none tracking-[0.01em] ring-offset-background transition-[transform,box-shadow-sm,background-color,border-color,color] duration-200 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-[18px] [&_svg]:shrink-0 active:scale-[0.98]",
  {
    variants: {
      variant: {
        default: "btn-gold",
        luxury: "btn-gold",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-border/70 bg-card/80 text-foreground shadow-xs hover:bg-muted/35 hover:text-foreground",
        secondary: "bg-secondary/85 text-secondary-foreground shadow-xs hover:bg-secondary",
        ghost: "bg-transparent text-foreground/80 shadow-none hover:bg-muted/45 hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "!h-12 !px-5",
        sm: "!h-11 !rounded-[12px] !px-4 !text-sm",
        lg: "!h-[50px] !px-6 !text-[15px]",
        icon: "!h-12 !w-12 !p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
